import React, { useEffect, useState, useMemo } from "react";
import { fmtBytes, fmtNum, ago } from "../utils/formatters";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { WanNameEditor } from "./WanNames";
import {
  TimeSeriesChart,
  ArcGauge,
  MiniKPI,
  BarChart,
  useTimeSeries,
} from "./DiagCharts";

// ── WAN Column ───────────────────────────────────────────────────────────────

function WANColumn({ wan, health, diagWan, stressImpairment, ts }) {
  const isImpaired = stressImpairment?.active;
  const n = wan.name;

  const latArr = ts.get(n + "_lat");
  const jitArr = ts.get(n + "_jit");
  const lossArr = ts.get(n + "_loss");
  const scoreArr = ts.get(n + "_score");
  const txArr = ts.get(n + "_txr");
  const rxArr = ts.get(n + "_rxr");

  const scoreColor = (health?.score_balanced ?? 0) >= 70 ? "#22c55e" : (health?.score_balanced ?? 0) >= 40 ? "#f59e0b" : "#ef4444";

  return (
    <div className="diag-wan-col">
      {/* Header */}
      <div className="diag-wan-col-header">
        <span className={"diag-wan-status-dot " + (wan.active ? "up" : "down")} />
        <WanNameEditor iface={wan.name} />
        {isImpaired && <span className="pill-stress" style={{ marginLeft: "auto" }}>STRESSED</span>}
      </div>

      {/* Score gauge */}
      {health && (
        <div className="diag-card" style={{ alignItems: "center" }}>
          <ArcGauge
            value={Math.round(health.score_balanced ?? 0)}
            max={100}
            label="Balanced Score"
            size={140}
          />
          <div className="diag-kpi-strip" style={{ justifyContent: "center" }}>
            <MiniKPI label="Latency" value={health.score_latency != null ? Math.round(health.score_latency) : "\u2014"} color="#6366f1" />
            <MiniKPI label="BW" value={health.score_bw != null ? Math.round(health.score_bw) : "\u2014"} color="#06b6d4" />
          </div>
        </div>
      )}

      {/* Live metrics */}
      {health && (
        <div className="diag-card">
          <div className="diag-card-title">Live Metrics</div>
          <div className="diag-kpi-strip">
            <MiniKPI
              label="Latency"
              value={health.latency_ms != null ? health.latency_ms.toFixed(1) : "\u2014"}
              unit=" ms"
              sparkData={latArr}
              color={health.latency_ms > 100 ? "#ef4444" : health.latency_ms > 30 ? "#f59e0b" : "#22c55e"}
            />
            <MiniKPI
              label="Jitter"
              value={health.jitter_ms != null ? health.jitter_ms.toFixed(1) : "\u2014"}
              unit=" ms"
              sparkData={jitArr}
              color={health.jitter_ms > 20 ? "#ef4444" : health.jitter_ms > 5 ? "#f59e0b" : "#22c55e"}
            />
          </div>
          <div className="diag-kpi-strip">
            <MiniKPI
              label="Loss"
              value={health.loss_ratio != null ? (health.loss_ratio * 100).toFixed(1) : "\u2014"}
              unit="%"
              sparkData={lossArr}
              color={health.loss_ratio > 0.05 ? "#ef4444" : health.loss_ratio > 0.01 ? "#f59e0b" : "#22c55e"}
            />
            <MiniKPI
              label="Bandwidth"
              value={health.measured_bw_mbps != null ? health.measured_bw_mbps.toFixed(1) : "\u2014"}
              unit=" Mbps"
              color="#3b82f6"
            />
          </div>
        </div>
      )}

      {/* Latency time series */}
      {latArr.length > 2 && (
        <div className="diag-card">
          <div className="diag-card-title">Latency History</div>
          <TimeSeriesChart
            series={[
              { label: "Latency", data: latArr, color: "#6366f1" },
              { label: "Jitter", data: jitArr, color: "#f59e0b" },
            ]}
            yLabel="ms"
            yMin={0}
            height={140}
            showLegend={true}
          />
        </div>
      )}

      {/* Throughput time series */}
      {txArr.length > 2 && (
        <div className="diag-card">
          <div className="diag-card-title">Throughput (KB/s)</div>
          <TimeSeriesChart
            series={[
              { label: "TX", data: txArr, color: "#6366f1" },
              { label: "RX", data: rxArr, color: "#22c55e" },
            ]}
            yLabel="KB/s"
            yMin={0}
            height={140}
          />
        </div>
      )}

      {/* Score trend */}
      {scoreArr.length > 2 && (
        <div className="diag-card">
          <div className="diag-card-title">Score Trend</div>
          <TimeSeriesChart
            series={[{ label: "Score", data: scoreArr, color: scoreColor }]}
            yLabel=""
            yMin={0}
            yMax={100}
            height={120}
            showLegend={false}
          />
        </div>
      )}

      {/* Interface info */}
      <div className="diag-card">
        <div className="diag-card-title">Interface Details</div>
        <div className="diag-iface-details">
          <div className="diag-iface-row">
            <span className="diag-iface-label">Interface</span>
            <span className="diag-iface-value">{wan.name}</span>
          </div>
          {wan.addresses && wan.addresses.length > 0 && (
            <div className="diag-iface-row">
              <span className="diag-iface-label">IP</span>
              <div className="diag-iface-value">{wan.addresses.map((a, i) => <div key={i}>{a}</div>)}</div>
            </div>
          )}
          {wan.mac && (
            <div className="diag-iface-row">
              <span className="diag-iface-label">MAC</span>
              <span className="diag-iface-value">{wan.mac}</span>
            </div>
          )}
          {wan.gateways && wan.gateways.length > 0 && (
            <div className="diag-iface-row">
              <span className="diag-iface-label">Gateway</span>
              <div className="diag-iface-value">{wan.gateways.map((g, i) => <div key={i}>{g}</div>)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Packet counters */}
      {diagWan && (
        <div className="diag-card">
          <div className="diag-card-title">Packet Counters</div>
          <BarChart
            items={[
              { label: "TX Pkts", value: diagWan.tx_packets || 0, color: "#6366f1" },
              { label: "RX Pkts", value: diagWan.rx_packets || 0, color: "#06b6d4" },
              { label: "TX Data", value: diagWan.tx_bytes || 0, color: "#8b5cf6" },
              { label: "RX Data", value: diagWan.rx_bytes || 0, color: "#14b8a6" },
            ]}
            height={20}
            formatValue={(v) => v > 1e6 ? fmtBytes(v) : fmtNum(v)}
          />
          <div className="diag-kpi-strip" style={{ marginTop: 8 }}>
            <MiniKPI label="Errors" value={fmtNum(diagWan.errors)} color={diagWan.errors > 0 ? "#ef4444" : "#22c55e"} />
            <MiniKPI label="Downs" value={fmtNum(diagWan.down_count)} color={diagWan.down_count > 0 ? "#f59e0b" : "#22c55e"} />
            <MiniKPI label="Last Up" value={ago(diagWan.last_up)} />
            <MiniKPI label="Last Down" value={ago(diagWan.last_down)} />
          </div>
        </div>
      )}

      {/* Active impairment overlay */}
      {isImpaired && (
        <div className="diag-card" style={{ borderColor: "#ef4444" }}>
          <div className="diag-card-title" style={{ color: "#ef4444" }}>Active Impairment</div>
          <div className="stress-active-summary">
            {stressImpairment.latency_ms > 0 && <span className="stress-tag">+{stressImpairment.latency_ms}ms latency</span>}
            {stressImpairment.jitter_ms > 0 && <span className="stress-tag">{"\u00B1"}{stressImpairment.jitter_ms}ms jitter</span>}
            {stressImpairment.loss_pct > 0 && <span className="stress-tag">{stressImpairment.loss_pct}% loss</span>}
            {stressImpairment.bandwidth_kbps > 0 && <span className="stress-tag">{fmtNum(stressImpairment.bandwidth_kbps)} kbps cap</span>}
            {stressImpairment.corrupt_pct > 0 && <span className="stress-tag">{stressImpairment.corrupt_pct}% corrupt</span>}
            {stressImpairment.duplicate_pct > 0 && <span className="stress-tag">{stressImpairment.duplicate_pct}% dup</span>}
            {stressImpairment.reorder_pct > 0 && <span className="stress-tag">{stressImpairment.reorder_pct}% reorder</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

export default function DiagWANPanel({ nodeApiUrl, selectedNode }) {
  const [wans, setWans] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [diagData, setDiagData] = useState(null);
  const [stressData, setStressData] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastFetch, setLastFetch] = useState(null);
  const ts = useTimeSeries(60);

  useEffect(() => {
    if (!selectedNode) { setWans([]); setLoading(false); return; }
    const controller = new AbortController();
    setLoading(true);

    const poll = async () => {
      try {
        const urls = ["/wans", "/health", "/diagnostics", "/stress/status"];
        const [wansRes, healthRes, diagRes, stressRes] = await Promise.all(
          urls.map((p) => {
            const u = nodeApiUrl(p);
            return u ? fetchWithTimeout(u, { signal: controller.signal }).then((r) => r.ok ? r.json() : null).catch(() => null) : Promise.resolve(null);
          })
        );
        if (controller.signal.aborted) return;
        if (wansRes) setWans(Array.isArray(wansRes.wans) ? wansRes.wans : []);
        if (healthRes) {
          setHealthData(healthRes);
          for (const w of healthRes.health || []) {
            const n = w.wan || w.name;
            ts.push(n + "_lat", w.latency_ms ?? 0);
            ts.push(n + "_jit", w.jitter_ms ?? 0);
            ts.push(n + "_loss", (w.loss_ratio ?? 0) * 100);
            ts.push(n + "_score", w.score_balanced ?? 0);
            ts.push(n + "_txr", (w.tx_bytes_per_sec ?? 0) / 1024);
            ts.push(n + "_rxr", (w.rx_bytes_per_sec ?? 0) / 1024);
          }
        }
        if (diagRes) setDiagData(diagRes);
        if (stressRes) setStressData(stressRes.impairments || {});
        setLastFetch(new Date());
      } catch (err) {
        if (err.name !== "AbortError") console.error("[DiagWAN]", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => { controller.abort(); clearInterval(timer); };
  }, [selectedNode, nodeApiUrl]); // eslint-disable-line

  const healthMap = useMemo(() => {
    const m = {};
    if (healthData?.health) for (const w of healthData.health) m[w.wan || w.name] = w;
    return m;
  }, [healthData]);

  const diagWanMap = useMemo(() => {
    if (!diagData?.wans) return {};
    const m = {};
    for (const [k, v] of Object.entries(diagData.wans)) m[v.name || k] = v;
    return m;
  }, [diagData]);

  if (!selectedNode) {
    return (
      <div className="panel diag-panel">
        <div className="panel-title">WAN Deep Dive</div>
        <div className="panel-sub">Select an edge node to inspect WAN interfaces.</div>
      </div>
    );
  }

  return (
    <div className="panel diag-panel">
      <div className="panel-title">WAN Deep Dive {"\u2014 "}{selectedNode}</div>
      <div className="panel-sub">
        Side-by-side WAN comparison with real-time charts, health scores, and impairment overlays.
        <span className="diag-refresh-ts">
          {lastFetch ? " Updated " + lastFetch.toLocaleTimeString() : "\u00A0"}
        </span>
      </div>

      {loading && wans.length === 0 && (
        <div className="diag-loading">{"Loading WAN data\u2026"}</div>
      )}

      <div className="diag-wan-compare">
        {wans.map((wan) => (
          <WANColumn
            key={wan.name}
            wan={wan}
            health={healthMap[wan.name]}
            diagWan={diagWanMap[wan.name]}
            stressImpairment={stressData[wan.name]}
            ts={ts}
          />
        ))}
      </div>

      {wans.length === 0 && !loading && (
        <div className="diag-empty">No WAN interfaces detected.</div>
      )}
    </div>
  );
}
